import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { colors } from '../../../styles/colors';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Fängt React-Rendering-Fehler ab und zeigt eine Fallback-UI
 * statt eines White Screens. Muss als Class Component implementiert
 * werden, da React Error Boundaries keine Hooks unterstützen.
 */
class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(_error: Error, _info: ErrorInfo) {
    // Platzhalter für zukünftigen Error-Reporting-Service (z.B. Sentry)
  }

  handleReload = () => {
    this.setState({ hasError: false });
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            backgroundColor: colors.background.default,
            px: 3,
          }}
        >
          <Typography variant="h5" sx={{ color: colors.text.primary, fontWeight: 600 }}>
            Ein unerwarteter Fehler ist aufgetreten
          </Typography>
          <Typography variant="body1" sx={{ color: colors.text.secondary, textAlign: 'center', maxWidth: 480 }}>
            Die Anwendung konnte nicht korrekt geladen werden.
            Bitte versuche es erneut.
          </Typography>
          <Button
            variant="contained"
            onClick={this.handleReload}
            sx={{
              mt: 2,
              backgroundColor: colors.brand.primary,
              '&:hover': { backgroundColor: colors.brand.primaryDark },
            }}
          >
            Zur Startseite
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
